/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

// Add third-party dependencies.
import { Box, Typography, Button, Divider, Link } from '@mui/material';
import {
  Article as ArticleIcon,
  Security as PrivacyIcon,
  History as HistoryIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Legal links section component for the Settings page.
 * Provides links to Terms of Service, Privacy Policy, and Changelog.
 *
 * @returns {React.ReactElement} The legal links section component
 *
 * @example
 * return (
 *   <LegalLinksSection />
 * );
 */
const LegalLinksSection: React.FC = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();

  /**
   * Handles navigation to changelog (internal page).
   */
  const handleChangelogNavigate = () => {
    navigate('/changelog');
  };

  return (
    <Box>
      {/* Section Header */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t('legal.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('legal.description')}
      </Typography>

      {/* Terms of Service */}
      <Box sx={{ mb: 2 }}>
        <Link
          href="/terms"
          target="_blank"
          underline="none"
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            p: 2,
            borderRadius: 1,
            color: 'inherit',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ArticleIcon sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              {t('legal.termsOfService')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('legal.viewTerms')}
            </Typography>
          </Box>
          <OpenInNewIcon fontSize="small" />
        </Link>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Privacy Policy */}
      <Box sx={{ mb: 2 }}>
        <Link
          href="/privacy"
          target="_blank"
          underline="none"
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            p: 2,
            borderRadius: 1,
            color: 'inherit',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <PrivacyIcon sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              {t('legal.privacyPolicy')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('legal.viewPrivacy')}
            </Typography>
          </Box>
          <OpenInNewIcon fontSize="small" />
        </Link>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Changelog */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="text"
          startIcon={<HistoryIcon />}
          endIcon={<OpenInNewIcon fontSize="small" />}
          onClick={handleChangelogNavigate}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            width: '100%',
            p: 2,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              Changelog
            </Typography>
            <Typography variant="caption" color="text.secondary">
              See what&apos;s new and what&apos;s been improved
            </Typography>
          </Box>
        </Button>
      </Box>

      {/* Additional Information */}
      <Box
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          mt: 3,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          Platform Information
        </Typography>
        <Typography variant="body2" color="text.secondary">
          These documents contain important information about using tools.dance.
          We recommend reviewing them periodically as they may be updated to
          reflect new features or legal requirements.
        </Typography>
      </Box>
    </Box>
  );
};

export default LegalLinksSection;
